import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "The Approach", href: "/#approach", id: "nav-link-approach" },
  { label: "Areas We Help", href: "/#areas", id: "nav-link-areas" },
  { label: "About", href: "/#about", id: "nav-link-doctor" },
  { label: "How It Works", href: "/#how-it-works", id: "nav-link-how" },
  { label: "Journal", href: "/#journal", id: "nav-link-journal" },
  { label: "FAQ", href: "/#faq", id: "nav-link-faq" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const [path, hash] = href.split("#");
    if (hash) {
      if (window.location.pathname !== path && path) {
        navigate(path);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(path);
    }
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled ? "bg-ivory/90 backdrop-blur-md shadow-[0_1px_0_0_#E2DCD0]" : "bg-ivory/60 backdrop-blur-sm"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-24 lg:h-28 flex items-center justify-between">
        <div className="flex items-center gap-8 flex-1">
          <button
            className="lg:hidden text-forest p-2 -ml-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary left">
            {LINKS.slice(0, 3).map((l) => (
              <button
                key={l.id}
                data-testid={l.id}
                onClick={() => go(l.href)}
                className="text-[13px] font-medium tracking-wide text-inkmuted hover:text-forest transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
        <Link
          to="/"
          aria-label="InSight Metabolics home"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <Logo className="h-14 sm:h-16 lg:h-[72px]" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-end" aria-label="Primary right">
          {LINKS.slice(3).map((l) => (
            <button
              key={l.id}
              data-testid={l.id}
              onClick={() => go(l.href)}
              className="text-[13px] font-medium tracking-wide text-inkmuted hover:text-forest transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/health-profile"
            data-testid="nav-cta-health-profile"
            className="ml-2 inline-flex items-center rounded-full bg-forest text-ivory text-[13px] font-semibold tracking-wide px-6 py-3 border border-gold/40 hover:bg-forestdeep transition-colors duration-300"
          >
            Start Your Health Profile
          </Link>
        </nav>
        <div className="lg:hidden flex-1" aria-hidden="true" />
      </div>
      {open && (
        <div className="lg:hidden bg-ivory border-t border-line px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`${l.id}-mobile`}
              onClick={() => go(l.href)}
              className="text-left text-base font-medium text-ink"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/health-profile"
            data-testid="nav-cta-health-profile-mobile"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex justify-center rounded-full bg-forest text-ivory text-sm font-semibold px-6 py-3.5 border border-gold/40"
          >
            Start Your Health Profile
          </Link>
        </div>
      )}
    </header>
  );
};
